'use client';

import { Container } from '@/components/container';
import { Header } from '@/components/header';
import { ECategory, ENotificationType } from '@/enums';
import { useDialog } from '@/hooks';
import { clearVoterData, setToken, setVoterData } from '@/store/slices';
import { AppDispatch, RootState } from '@/store/store';
import { createNewVoter, initiateVoterSearch, selfTransfer, verifyOtp } from '@/store/thunks';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, MapIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { EnterOtp, NewVoterForm, SearchForm, TransferForm } from './forms';
import {
  NewVoterFormValues,
  OTPFormValues,
  SearchFormValues,
  TransferFormValues,
  newVoterSchema,
  searchSchema,
  transferSchema
} from './schema';
import { VoterDetails } from './voter-details';
import { VoterLocation } from './voter-location';

export const Home = () => {
  const { t } = useTranslation();
  const { onOpen, onClose } = useDialog();
  const dispatch = useDispatch<AppDispatch>();

  const { voter, isLoading, token } = useSelector((state: RootState) => state.voters);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [showNewVoterForm, setShowNewVoterForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const searchForm = useForm<SearchFormValues>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      category: ECategory.LOCAL,
      nationalId: '',
      phoneNumber: '',
      motherName: '',
      type: ENotificationType.SMS,
      email: '',
      phoneIntl: ''
    }
  });

  const transferForm = useForm<TransferFormValues>({
    resolver: zodResolver(transferSchema),
    mode: 'onChange',
    defaultValues: {
      province: '',
      district: '',
      sector: '',
      cell: '',
      village: ''
    }
  });

  const newVoterForm = useForm<NewVoterFormValues>({
    resolver: zodResolver(newVoterSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      fatherName: '',
      motherName: '',
      email: '',
      phoneNumber: '',
      votingVillageId: '',
      originalVillageId: '',
      genderId: '',
      dateOfBirth: new Date(new Date().getFullYear() - 16, 0, 1).toISOString().split('T')[0],
      placeOfBirth: '',
      address: '',
      passportNumber: '',
      drivingLicenseNumber: '',
      otherIdType: '',
      otherIdNumber: '',
      reason: '',
      province: '',
      district: '',
      sector: '',
      cell: '',
      village: ''
    }
  });

  useEffect(() => {
    if (voter?.village) {
      transferForm.reset({
        province: voter.village.cell.sector.district.province.id,
        district: voter.village.cell.sector.district.id,
        sector: voter.village.cell.sector.id,
        cell: voter.village.cell.id,
        village: voter.village.id
      });
    }
  }, [voter, transferForm]);

  const handleSearch = async (data: SearchFormValues) => {
    try {
      setSubmitted(true);

      await dispatch(initiateVoterSearch(data)).unwrap();

      onOpen();
      toast.success('Please check your phone or email for the verification code.');
    } catch (error) {
      toast.error('Failed to search voter information');
      setSubmitted(true);
    }
  };

  const handleVerify = async (data: OTPFormValues) => {
    try {
      const payload = {
        nationalId: searchForm.getValues('nationalId'),
        otpCode: data.otp
      };

      const resultAction = await dispatch(verifyOtp(payload));
      if (verifyOtp.fulfilled.match(resultAction)) {
        onClose();

        const responseData = resultAction.payload.data;
        dispatch(setVoterData(responseData.voter));
        dispatch(setToken(responseData.token as string));

        toast.success('Your identity has been verified successfully.');
      } else {
        toast.error((resultAction.payload as string) || 'Failed to verify OTP');
      }
    } catch (error) {
      toast.error('An unexpected error occurred during verification');
    }
  };

  const handleCancel = () => {
    searchForm.setValue('category', ECategory.LOCAL);
    searchForm.setValue('phoneNumber', '');
    searchForm.setValue('nationalId', '');
    searchForm.setValue('motherName', '');
    searchForm.setValue('type', ENotificationType.SMS);
    searchForm.setValue('email', '');
    searchForm.setValue('phoneIntl', '');

    // Reset state
    dispatch(clearVoterData());
    setShowTransferForm(false);
    setSubmitted(false);
  };

  const handleTransfer = async (villageId: string) => {
    if (token) {
      try {
        const payload = {
          villageId: villageId
        };

        await dispatch(selfTransfer(payload)).unwrap();
        toast.success('Your voting location has been updated successfully.');
        setShowTransferForm(false);
      } catch (error: unknown) {
        toast.error(typeof error === 'string' ? error : 'Failed to update your voting location.');
        console.error('Transfer error:', error);
      }
    }
  };

  const handleNewVoterSubmit = async (data: NewVoterFormValues) => {
    try {
      const payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        fatherName: data.fatherName,
        motherName: data.motherName,
        email: data.email || undefined,
        phoneNumber: data.phoneNumber || undefined,
        votingVillageId: data.votingVillageId,
        originalVillageId: data.originalVillageId || undefined,
        genderId: data.genderId,
        dateOfBirth: new Date(data.dateOfBirth).toISOString(),
        placeOfBirth: data.placeOfBirth || undefined,
        address: data.address || undefined,
        passportNumber: data.passportNumber || undefined,
        drivingLicenseNumber: data.drivingLicenseNumber || undefined,
        otherIdType: data.otherIdType || undefined,
        otherIdNumber: data.otherIdNumber || undefined,
        reason: data.reason || undefined
      };

      await dispatch(createNewVoter(payload)).unwrap();
      toast.success(
        'Your registration has been submitted successfully. You will be contacted once processed.'
      );
      setShowNewVoterForm(false);
      newVoterForm.reset();
    } catch (error: unknown) {
      toast.error(typeof error === 'string' ? error : 'Failed to submit registration');
    }
  };

  const handleNewVoterCancel = () => {
    setShowNewVoterForm(false);
    newVoterForm.reset();
  };

  return (
    <>
      <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#f1f6fc] text-[#11213f]">
        <Header />

        <main className="relative flex-1">
          <div
            className="pointer-events-none absolute -bottom-48 -left-44 h-[620px] w-[620px] rounded-full bg-[#ddeafb]"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute left-[37%] top-16 h-64 w-64 rounded-full bg-white/45 blur-3xl"
            aria-hidden="true"
          />

          <div className="relative py-10 sm:py-14 lg:py-[30px]">
            <Container className="max-w-[1440px]">
              {!showNewVoterForm ? (
                <div
                  className={
                    voter
                      ? 'grid w-full gap-8 lg:grid-cols-[390px_minmax(0,1fr)]'
                      : 'grid w-full items-center gap-12 lg:grid-cols-[minmax(0,1fr)_500px] xl:gap-20'
                  }>
                  {!voter && (
                    <section className="hidden lg:grid relative mx-auto max-w-[650px] py-4 lg:mx-0 lg:py-16">
                      <h1 className="max-w-[640px] text-[48px] font-bold leading-[0.98] tracking-[-0.055em] text-[#10213f] sm:text-[64px] lg:text-[68px]">
                        {t('TextWelcomeToThe')}{' '}
                        <span className="text-[#2374cf]">{t('TextVoterPortal')}</span>
                      </h1>
                      <p className="mt-7 max-w-[610px] text-base leading-7 text-[#71809a] sm:text-lg sm:leading-8">
                        {t('TextPortalDescription')}
                      </p>
                      <div className="mt-9 flex flex-wrap gap-x-8 gap-y-4 text-sm font-semibold text-[#35435c]">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#dff5e7] text-[#168c50]">
                            <Check className="h-4 w-4" strokeWidth={2.5} />
                          </span>
                          {t('TextSecureAndTrusted')}
                        </div>
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#dff5e7] text-[#168c50]">
                            <Check className="h-4 w-4" strokeWidth={2.5} />
                          </span>
                          {t('TextSimpleToUse')}
                        </div>
                      </div>
                    </section>
                  )}

                  <section className="w-full">
                    <div className="rounded-md border border-[#e4eaf3] bg-white p-6 shadow-[0_22px_60px_rgba(26,53,91,0.08)] sm:p-9">
                      <div className="space-y-7">
                        <div>
                          <h2 className="mb-3 text-[16px] font-medium tracking-[-0.035em] text-[#14233f] sm:text-[32px]">
                            {t('TextVoterSelfTransfer')}
                          </h2>
                          <p className="text-[14px] leading-4 text-[#7a88a0]">
                            {t('TextVoterTransferDescription')}
                          </p>
                        </div>

                        <FormProvider {...searchForm}>
                          <SearchForm
                            isLoading={isLoading}
                            submitted={submitted}
                            onSearch={handleSearch}
                            onCancel={handleCancel}
                          />
                        </FormProvider>

                        <div className="border-t border-[#edf1f6] pt-6">
                          <button
                            type="button"
                            onClick={() => setShowNewVoterForm(true)}
                            className="w-full text-center text-sm font-semibold text-[#2474d2] transition-colors hover:text-[#155cad] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2474d2]/30">
                            {t('TextDontHaveNationalId')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>

                  {voter && (
                    <section className="flex w-full flex-col">
                      <div className="overflow-hidden rounded-md border border-[#e4eaf3] bg-white shadow-[0_22px_60px_rgba(26,53,91,0.08)]">
                        <VoterDetails voter={voter} />

                        <div className="border-t border-[#edf1f6] p-6 sm:p-7">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <h4 className="flex items-center gap-3 text-lg font-semibold text-[#172641]">
                              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eaf3ff]">
                                <MapIcon className="h-5 w-5 text-[#2374cf]" strokeWidth={2} />
                              </div>
                              {t('TextVotingInfo')}
                            </h4>

                            {!showTransferForm && (
                              <button
                                type="button"
                                onClick={() => setShowTransferForm(true)}
                                className="rounded-md bg-[#338AF3] hover:bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white  transition-colors">
                                {t('TextUpdateVotingLocation')}
                              </button>
                            )}
                          </div>

                          <div className="mt-6">
                            {showTransferForm ? (
                              <div className="rounded-md border border-[#e2e9f3] bg-white p-5 shadow-[0_10px_30px_rgba(26,53,91,0.045)] sm:p-6">
                                <FormProvider {...transferForm}>
                                  <TransferForm
                                    onSubmitVillage={handleTransfer}
                                    onCancel={() => setShowTransferForm(false)}
                                  />
                                </FormProvider>
                              </div>
                            ) : (
                              <VoterLocation village={voter.village} />
                            )}
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              ) : (
                <div className="max-w-none">
                  <FormProvider {...newVoterForm}>
                    <NewVoterForm
                      isLoading={isLoading}
                      onSubmit={handleNewVoterSubmit}
                      onCancel={handleNewVoterCancel}
                    />
                  </FormProvider>
                </div>
              )}
            </Container>
          </div>
        </main>
      </div>

      <EnterOtp onVerify={handleVerify} />
    </>
  );
};
